/**
 * useCardEditorState — Combined hook for the card editor.
 *
 * Owns ALL card editor state: template, draft persistence, dirty tracking,
 * fetch from backend, and publish mutation.
 *
 * CardEditorPanel becomes a controlled component receiving template +
 * onTemplateChange from this hook.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  cpGetCardTemplate,
  cpPublishCardDesign,
  type CardTemplateResponse,
} from '@/lib/api/controlPlanePageConfigClient'
import type { CardTemplate } from './card-editor-types'
import { DEFAULT_CARD_TEMPLATE } from './card-editor-types'

// ---------------------------------------------------------------------------
// Draft persistence (localStorage)
// ---------------------------------------------------------------------------

const DRAFT_KEY_PREFIX = 'card-editor-draft-'

function draftKey(tenantId: string): string {
  return `${DRAFT_KEY_PREFIX}${tenantId}`
}

function loadDraft(tenantId: string): CardTemplate | null {
  try {
    const raw = localStorage.getItem(draftKey(tenantId))
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || !Array.isArray(parsed.sections)) return null
    return parsed as CardTemplate
  } catch {
    return null
  }
}

function saveDraftToStorage(tenantId: string, template: CardTemplate) {
  try {
    localStorage.setItem(draftKey(tenantId), JSON.stringify(template))
  } catch {
    // localStorage full or unavailable — ignore
  }
}

function clearDraftFromStorage(tenantId: string) {
  try {
    localStorage.removeItem(draftKey(tenantId))
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const CARD_TEMPLATE_QUERY_KEY = ['card-template'] as const

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useCardEditorState(tenantId: string | null) {
  const queryClient = useQueryClient()
  const saveTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const initializedRef = useRef(false)

  // 1. Fetch saved template from backend
  const { data: savedTemplate, isLoading, isError, error, refetch } = useQuery({
    queryKey: [...CARD_TEMPLATE_QUERY_KEY, tenantId],
    queryFn: () => cpGetCardTemplate(),
    enabled: !!tenantId,
  })

  // 2. Local template state — initialized from draft or saved template
  const [template, setTemplateRaw] = useState<CardTemplate>(DEFAULT_CARD_TEMPLATE)

  // Initialize template once when saved data arrives (or from draft)
  useEffect(() => {
    if (initializedRef.current || !tenantId) return

    // Try draft first
    const draft = loadDraft(tenantId)
    if (draft) {
      setTemplateRaw(draft)
      initializedRef.current = true
      return
    }

    // Use saved template when available
    if (savedTemplate) {
      setTemplateRaw(savedTemplate as CardTemplate)
      initializedRef.current = true
    }
  }, [tenantId, savedTemplate])

  // 3. Template setter that also triggers draft save
  const setTemplate = useCallback(
    (next: CardTemplate) => {
      setTemplateRaw(next)
    },
    [],
  )

  // 4. Auto-save draft to localStorage (debounced 500ms)
  useEffect(() => {
    if (!tenantId || !initializedRef.current) return
    clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => {
      saveDraftToStorage(tenantId, template)
    }, 500)
    return () => clearTimeout(saveTimerRef.current)
  }, [template, tenantId])

  // 5. Dirty tracking — compare current template to saved template
  const savedJson = useMemo(
    () => JSON.stringify(savedTemplate ?? DEFAULT_CARD_TEMPLATE),
    [savedTemplate],
  )
  const currentJson = useMemo(() => JSON.stringify(template), [template])
  const isDirty = initializedRef.current && savedJson !== currentJson

  // 6. Publish mutation
  const publishMutation = useMutation({
    mutationFn: (t: CardTemplate) =>
      cpPublishCardDesign(t as CardTemplateResponse),
    onSuccess: (result) => {
      // Update the query cache with the published template
      queryClient.setQueryData(
        [...CARD_TEMPLATE_QUERY_KEY, tenantId],
        result,
      )
      // Clear draft
      if (tenantId) clearDraftFromStorage(tenantId)
      toast.success('Card design published')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to publish card design')
    },
  })

  const publish = useCallback((opts?: { onSuccess?: () => void }) => {
    publishMutation.mutate(template, { onSuccess: opts?.onSuccess })
  }, [publishMutation, template])

  const clearDraft = useCallback(() => {
    if (tenantId) clearDraftFromStorage(tenantId)
  }, [tenantId])

  return {
    template,
    setTemplate,
    isLoading,
    isError,
    error,
    refetch,
    isDirty,
    publish,
    isPublishing: publishMutation.isPending,
    clearDraft,
  }
}
